/**
 * 報酬台帳 (commission_records) の hooks (v3.2.8 / T6)。
 *
 * 物理コレクション (subcollection):
 *   - 紹介者: mail_contacts/{contactId}/referral_records/{recordId}
 *   - SA / 社員: admins/{uid}/sales_records/{recordId}
 *
 * 台帳画面では collectionGroup クエリで両方を取得し、UI 側で merge して表示する。
 */
import type { CommissionRecord, Deal, DealStage } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, collectionGroup, documentId, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import type { AccountingNotifyOutcome } from './useTransferRequests';

import { db, functions } from '@/lib/firebase';

export interface CommissionRecordWithRef extends CommissionRecord {
  /** Firestore document ID */
  id: string;
  /** 親パスを含むフルパス (subcollection 経路を示す) — 編集 Callable で使う */
  path: string;
}

export function useCommissionRecords() {
  return useQuery({
    queryKey: ['commission_records', 'all'],
    queryFn: async (): Promise<CommissionRecordWithRef[]> => {
      // 2 つの subcollection を並列取得 (collectionGroup index ビルド待ち回避のため orderBy 無し → クライアント側 sort)
      const [salesSnap, referralSnap] = await Promise.all([
        getDocs(collectionGroup(db, 'sales_records')),
        getDocs(collectionGroup(db, 'referral_records')),
      ]);
      const merge = (snap: typeof salesSnap): CommissionRecordWithRef[] =>
        snap.docs.map((d) => ({
          id: d.id,
          path: d.ref.path,
          ...(d.data() as CommissionRecord),
        }));
      const merged = [...merge(salesSnap), ...merge(referralSnap)];
      // paidAt 降順で再ソート
      merged.sort((a, b) => {
        const at =
          typeof a.paidAt === 'object' && 'toMillis' in a.paidAt
            ? a.paidAt.toMillis()
            : new Date(a.paidAt as string).getTime();
        const bt =
          typeof b.paidAt === 'object' && 'toMillis' in b.paidAt
            ? b.paidAt.toMillis()
            : new Date(b.paidAt as string).getTime();
        return bt - at;
      });
      return merged;
    },
  });
}

/**
 * 自分の sales_records (admins/{uid}/sales_records) のみを取得 (v3.2.11 / SA Dashboard Phase 1)。
 *
 * SA / 社員が自分の報酬のみを確認できるダッシュボード用。
 * Firestore Rules で本人 + owner のみ read 可能になっているため、
 * ここでは特定 uid 配下の subcollection を直接クエリする。
 */
export function useMyCommissionRecords(uid: string | null | undefined) {
  return useQuery({
    queryKey: ['commission_records', 'mine', uid],
    enabled: !!uid,
    queryFn: async (): Promise<CommissionRecordWithRef[]> => {
      if (!uid) return [];
      const snap = await getDocs(collection(db, 'admins', uid, 'sales_records'));
      const records: CommissionRecordWithRef[] = snap.docs.map((d) => ({
        id: d.id,
        path: d.ref.path,
        ...(d.data() as CommissionRecord),
      }));
      records.sort((a, b) => {
        const at =
          typeof a.paidAt === 'object' && 'toMillis' in a.paidAt
            ? a.paidAt.toMillis()
            : new Date(a.paidAt as string).getTime();
        const bt =
          typeof b.paidAt === 'object' && 'toMillis' in b.paidAt
            ? b.paidAt.toMillis()
            : new Date(b.paidAt as string).getTime();
        return bt - at;
      });
      return records;
    },
  });
}

/**
 * 報酬レコードに関連する Deal の最新 stage を一括取得 (v3.2.13.5)。
 *
 * 用途: 報酬台帳で「現在の商談ステータス（入金 / 発注 / 発送 / 納品）」を表示するため。
 * Trigger 時のスナップショットではなくリアルタイム取得（毎レンダー毎に IN クエリ）。
 *
 * Firestore IN クエリは最大 30 件まで → dealIds.length / 30 でチャンク分割。
 */
export function useDealStagesByIds(dealIds: string[]) {
  // ユニーク化 + ソートで安定化 (queryKey が無限ループしないように)
  const uniq = Array.from(new Set(dealIds.filter(Boolean))).sort();
  return useQuery({
    queryKey: ['deals', 'stages_by_ids', uniq.join(',')],
    enabled: uniq.length > 0,
    queryFn: async (): Promise<Record<string, DealStage>> => {
      if (uniq.length === 0) return {};
      const out: Record<string, DealStage> = {};
      // Firestore IN は最大 30 個
      for (let i = 0; i < uniq.length; i += 30) {
        const chunk = uniq.slice(i, i + 30);
        const snap = await getDocs(
          query(collection(db, 'deals'), where(documentId(), 'in', chunk)),
        );
        for (const d of snap.docs) {
          const data = d.data() as Deal;
          if (data.stage) out[d.id] = data.stage;
        }
      }
      return out;
    },
  });
}

// ─── Phase 2 Callable ───

const updateRecordFn = httpsCallable<
  {
    recordPath: string;
    bankAccountId?: string | null;
    finalAmountOverride?: number | null;
    notes?: string | null;
  },
  { ok: true }
>(functions, 'updateCommissionRecord');

export function useUpdateCommissionRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof updateRecordFn>[0]) => {
      const r = await updateRecordFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission_records'] }),
  });
}

const requestPaymentFn = httpsCallable<
  { recordPath: string },
  {
    ok: boolean;
    mailSent: boolean;
    reason?: string;
    /** 2026-10-26 追加: 経理担当への通知のチャネル別結果 */
    notify?: AccountingNotifyOutcome;
  }
>(functions, 'requestCommissionPayment');

export function useRequestCommissionPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof requestPaymentFn>[0]) => {
      const r = await requestPaymentFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission_records'] }),
  });
}

const markPaidFn = httpsCallable<{ recordPath: string; paidOutAt?: string | null }, { ok: true }>(
  functions,
  'markCommissionRecordPaid',
);

export function useMarkCommissionRecordPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof markPaidFn>[0]) => {
      const r = await markPaidFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission_records'] }),
  });
}
